import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import ColorThief from 'colorthief';
import { PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { clientPortalPublicAPI } from '../api/clientPortal';
import ScrollToTop from '../components/ScrollToTop';
import Footer from '../components/Footer';
import YouTubeEstimateTooltip from '../components/YouTubeEstimateTooltip';
import PlatformIcon from '../components/PlatformIcon';
import { reachOf, isStreamContent, impressionsFor } from '../utils/reportMetrics';

// ─── Helpers ───────────────────────────────────────────────────────────────

const isLightColor = (hex) => {
  if (!hex) return true;
  const c = hex.replace('#', '');
  const r = parseInt(c.substr(0, 2), 16);
  const g = parseInt(c.substr(2, 2), 16);
  const b = parseInt(c.substr(4, 2), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5;
};

const rgbToHex = (r, g, b) =>
  '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');

const formatNumber = (num) => {
  if (!num || num === 0) return '0';
  return num.toLocaleString();
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatPlatformName = (platform) => {
  const names = { twitch: 'Twitch', kick: 'Kick', youtube: 'YouTube', tiktok: 'TikTok', twitter: 'Twitter', instagram: 'Instagram' };
  return names[platform?.toLowerCase()] || platform;
};

const PLATFORM_COLORS = {
  youtube: '#FF0000',
  twitch: '#9146FF',
  kick: '#00E70A',
  tiktok: '#69C9D0',
  instagram: '#E4405F',
  twitter: '#1DA1F2',
  facebook: '#1877F2',
};

const getPlatformColor = (platform) => PLATFORM_COLORS[platform?.toLowerCase()] || '#6B7280';

const SC_PINK = '#fe2388';

// Default branding
const defaultBranding = {
  primaryColor: '#4f46e5',
  secondaryColor: '#8b5cf6',
  backgroundColor: '#ffffff',
  textColor: '#1f2937',
  accentColor: '#10b981',
  logoUrl: null,
  bannerUrl: null,
  fontFamily: 'Helvetica',
  backgroundImageMode: 'none',
  backgroundImageUrl: null,
};

// ─── AnimatedNumber ────────────────────────────────────────────────────────

const AnimatedNumber = ({ value, duration = 1500 }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const hasAnimated = useRef(false);
  const prevValue = useRef(value);

  // Re-animate when value changes (e.g. switching tabs)
  useEffect(() => {
    if (hasAnimated.current && prevValue.current !== value) {
      const from = prevValue.current || 0;
      const target = typeof value === 'number' ? value : parseInt(value, 10) || 0;
      prevValue.current = value;
      if (target === 0) { setDisplay(0); return; }

      const start = performance.now();
      const animate = (now) => {
        const elapsed = now - start;
        const progress = Math.min(elapsed / 400, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setDisplay(Math.round(from + (target - from) * eased));
        if (progress < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    }
  }, [value]);

  // Initial animation on scroll into view
  useEffect(() => {
    if (!ref.current || hasAnimated.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          prevValue.current = value;
          const target = typeof value === 'number' ? value : parseInt(value, 10) || 0;
          if (target === 0) { setDisplay(0); return; }

          const start = performance.now();
          const animate = (now) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setDisplay(Math.round(eased * target));
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value, duration]);

  return <span ref={ref}>{display.toLocaleString()}</span>;
};

// ─── GlassCard ─────────────────────────────────────────────────────────────

const GlassCard = ({ children, className = '', style = {}, onClick, glow = false }) => (
  <div
    className={`rounded-2xl backdrop-blur-xl ${className}`}
    onClick={onClick}
    style={{
      background: 'rgba(255, 255, 255, 0.08)',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      boxShadow: glow
        ? '0 8px 32px rgba(254, 35, 136, 0.15), inset 0 1px 0 rgba(255,255,255,0.1)'
        : '0 8px 32px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255,255,255,0.1)',
      ...style,
    }}
  >
    {children}
  </div>
);

// ─── DonutChart ────────────────────────────────────────────────────────────

const DonutChart = ({ data, centerLabel, centerValue, title }) => {
  const [hovered, setHovered] = useState(null);

  return (
    <GlassCard className="p-4 flex flex-col items-center">
      <h3 className="text-sm font-semibold text-white/70 mb-2">{title}</h3>

      {/* Hover tooltip - fixed above the chart */}
      <div className="h-6 flex items-center justify-center">
        {hovered !== null && data[hovered] && (
          <div className="flex items-center gap-2 text-xs">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: data[hovered].color }} />
            <span className="text-white font-medium">{data[hovered].name}</span>
            <span className="text-white/50">{formatNumber(data[hovered].value)} ({data[hovered].percentage}%)</span>
          </div>
        )}
      </div>

      <div className="relative" style={{ width: 180, height: 180 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={55}
              outerRadius={75}
              paddingAngle={2}
              dataKey="value"
              stroke="none"
              onMouseEnter={(_, index) => setHovered(index)}
              onMouseLeave={() => setHovered(null)}
            >
              {data.map((entry, i) => (
                <Cell key={i} fill={entry.color} opacity={hovered !== null && hovered !== i ? 0.4 : 1} style={{ transition: 'opacity 0.2s' }} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        {/* Center label */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-2xl font-bold text-white">{centerValue}</span>
          <span className="text-xs text-white/50">{centerLabel}</span>
        </div>
      </div>
      {/* Legend */}
      <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 mt-2">
        {data.map((entry, i) => (
          <div key={i} className="flex items-center gap-1 text-xs cursor-pointer"
            onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}>
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-white/60">{entry.name}</span>
          </div>
        ))}
      </div>
    </GlassCard>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────

const ClientPortalPublicV2 = () => {
  const { accessToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [portal, setPortal] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [branding, setBranding] = useState(null);
  const [extractedColors, setExtractedColors] = useState(null);
  const [summary, setSummary] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [keyTracking, setKeyTracking] = useState(null);
  const [content, setContent] = useState([]);

  const [showAllStreams, setShowAllStreams] = useState(false);
  const [showAllVideos, setShowAllVideos] = useState(false);
  const [showAllCreators, setShowAllCreators] = useState(false);
  const [streamSort, setStreamSort] = useState({ key: null, direction: 'desc' });
  const [videoSort, setVideoSort] = useState({ key: null, direction: 'desc' });
  const [gameCoverUrl, setGameCoverUrl] = useState(null);
  const [dateRange, setDateRange] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [contentTab, setContentTab] = useState('organic');
  const [sponsoredROI, setSponsoredROI] = useState(null);
  const [liveByCampaign, setLiveByCampaign] = useState({});

  const effectiveBranding = extractedColors && !branding?.primaryColor ? extractedColors : branding;
  const theme = { ...defaultBranding, ...effectiveBranding };

  // ─── Sort helpers ──────────────────────────────────────────────────────

  const sortItems = (items, sortState) => {
    if (!sortState.key) return items;
    return [...items].sort((a, b) => {
      let aVal, bVal;
      if (sortState.key === 'date') {
        aVal = new Date(a.postDate || 0).getTime();
        bVal = new Date(b.postDate || 0).getTime();
      } else if (sortState.key === 'engagementRate') {
        aVal = a.engagementRate || 0;
        bVal = b.engagementRate || 0;
      } else {
        aVal = a[sortState.key] || 0;
        bVal = b[sortState.key] || 0;
      }
      if (aVal === bVal) return 0;
      return sortState.direction === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });
  };

  const handleSort = (sortState, setSortState, key) => {
    if (sortState.key === key) {
      if (sortState.direction === 'desc') setSortState({ key, direction: 'asc' });
      else setSortState({ key: null, direction: 'desc' });
    } else {
      setSortState({ key, direction: 'desc' });
    }
  };

  const SortableHeader = ({ label, sortKey, sortState, setSortState, align = 'right', tooltip, thStyle, thClass }) => {
    const isActive = sortState.key === sortKey;
    return (
      <th className={`${thClass} cursor-pointer select-none hover:opacity-80 transition-opacity`} style={thStyle}
        onClick={() => handleSort(sortState, setSortState, sortKey)}>
        <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
          <span>{label}</span>
          {tooltip}
          <span className="inline-flex flex-col text-[8px] leading-[8px] ml-0.5" style={{ opacity: isActive ? 1 : 0.3 }}>
            <span style={{ opacity: isActive && sortState.direction === 'asc' ? 1 : 0.4 }}>&#9650;</span>
            <span style={{ opacity: isActive && sortState.direction === 'desc' ? 1 : 0.4 }}>&#9660;</span>
          </span>
        </div>
      </th>
    );
  };

  // ─── Color extraction ─────────────────────────────────────────────────

  const extractColorsFromImage = (imgElement) => {
    try {
      const colorThief = new ColorThief();
      const palette = colorThief.getPalette(imgElement, 6);
      if (palette && palette.length >= 3) {
        const colors = palette.map(rgb => rgbToHex(rgb[0], rgb[1], rgb[2]));
        const darkestColor = palette.reduce((darkest, current) => {
          const currentLum = (0.299 * current[0] + 0.587 * current[1] + 0.114 * current[2]) / 255;
          const darkestLum = (0.299 * darkest[0] + 0.587 * darkest[1] + 0.114 * darkest[2]) / 255;
          return currentLum < darkestLum ? current : darkest;
        });
        const textColor = rgbToHex(darkestColor[0], darkestColor[1], darkestColor[2]);
        setExtractedColors({
          primaryColor: colors[0],
          secondaryColor: colors[1],
          accentColor: colors[2],
          textColor: isLightColor(textColor) ? '#1f2937' : textColor,
          backgroundColor: '#ffffff',
        });
      }
    } catch (err) {
      console.log('Could not extract colors from cover image:', err);
    }
  };

  // ─── Data fetching ────────────────────────────────────────────────────

  useEffect(() => { fetchAllData(); }, [accessToken]);

  // Poll the Live Now data so CCV / Peak CCV tick without a manual page
  // refresh. Aligned to the backend poller cadence (~5 min) — polling faster
  // wouldn't surface newer numbers, it'd just add load. Fails silently per
  // campaign and clears on unmount so it can't pile up or overload.
  useEffect(() => {
    if (campaigns.length === 0) return;
    const interval = setInterval(() => { fetchLiveData(campaigns); }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [campaigns, accessToken]);

  useEffect(() => {
    if (campaigns.length > 0) {
      const firstGameWithCover = campaigns.find(c => c.game?.coverImageUrl || c.game?.backgroundImageUrl);
      if (firstGameWithCover?.game) {
        const game = firstGameWithCover.game;
        let backgroundUrl = game.backgroundImageUrl;
        if (!backgroundUrl && game.coverImageUrl) {
          const coverUrl = game.coverImageUrl;
          if (coverUrl.includes('images.igdb.com') && coverUrl.includes('/t_')) {
            backgroundUrl = coverUrl.replace(/\/t_[^/]+\//, '/t_720p/');
          } else {
            backgroundUrl = coverUrl;
          }
        }
        if (backgroundUrl) setGameCoverUrl(backgroundUrl);

        if (!branding?.primaryColor && game.coverImageUrl) {
          const img = new Image();
          img.crossOrigin = 'Anonymous';
          img.onload = () => extractColorsFromImage(img);
          img.src = game.coverImageUrl;
        }
      }
    }
  }, [campaigns, branding]);

  // Fetch just the Live Now data per campaign. Extracted so it can be polled
  // on an interval without re-fetching the whole portal payload.
  const fetchLiveData = async (campaignList) => {
    const campaignIds = (campaignList || []).map(c => c.id);
    if (campaignIds.length === 0) return;
    const entries = await Promise.all(campaignIds.map(id =>
      clientPortalPublicAPI.getLive(accessToken, id)
        .then(r => [id, r.data?.streams || []])
        .catch(() => [id, null]) // null = fetch failed; keep previous data
    ));
    setLiveByCampaign(prev => {
      const next = { ...prev };
      for (const [id, streams] of entries) {
        if (streams === null) continue;        // failed fetch: don't clobber
        if (streams.length > 0) next[id] = streams;
        else delete next[id];                  // no longer live: drop it
      }
      return next;
    });
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await clientPortalPublicAPI.getAll(accessToken);
      const data = response.data;
      setPortal(data.portal);
      setCampaigns(data.campaigns);
      setBranding(data.branding);
      setSummary(data.summary);
      setDocuments(data.documents);
      setContent(data.content);
      setKeyTracking(data.keyTracking);
      setSponsoredROI(data.sponsoredROI || null);

      // Fetch Live Now per campaign — runs in parallel, fails silently per
      // campaign so one broken fetch doesn't take down the rest.
      fetchLiveData(data.campaigns);
    } catch (err) {
      console.error('Error fetching portal:', err);
      if (err.response?.status === 404) setError('Portal not found. Please check the link and try again.');
      else if (err.response?.status === 403) setError('This portal is currently inactive.');
      else setError('Failed to load portal. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Date range filtering ────────────────────────────────────────────

  const getDateRangeBounds = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    switch (dateRange) {
      case 'today': return { start: today, end: now, label: 'Today' };
      case 'week': return { start: new Date(today.getTime() - 7 * 86400000), end: now, label: 'Last 7 Days' };
      case 'month': return { start: new Date(today.getTime() - 30 * 86400000), end: now, label: 'Last 30 Days' };
      case 'custom':
        if (customStartDate && customEndDate)
          return { start: new Date(customStartDate), end: new Date(customEndDate + 'T23:59:59'), label: `${formatDate(customStartDate)} - ${formatDate(customEndDate)}` };
        return null;
      default: return null;
    }
  };

  const filteredContent = (() => {
    const bounds = getDateRangeBounds();
    if (!bounds || !content) return content;
    return content.filter(c => {
      const date = new Date(c.postDate || c.createdAt);
      return date >= bounds.start && date <= bounds.end;
    });
  })();

  const contentBeforeTab = dateRange === 'all' ? content : filteredContent;
  const contentToDisplay = contentTab === 'organic'
    ? contentBeforeTab.filter(item => !item.isPaid)
    : contentTab === 'sponsored'
      ? contentBeforeTab.filter(item => item.isPaid)
      : contentBeforeTab;

  const filteredStats = (() => {
    if (!contentToDisplay || contentToDisplay.length === 0) {
      return { totalViews: 0, youtubeViews: 0, tiktokViews: 0, instagramViews: 0, twitchViews: 0,
        totalEngagement: 0, totalWatchHours: 0, totalContentCount: 0, totalCreators: 0,
        impressions: 0,
        avgCcv: 0, peakCcv: 0, totalVodViews: 0, platformBreakdown: [],
        creatorsWithCoverage: 0, creatorsWithoutCoverage: 0, coverageRate: 0 };
    }
    const creatorIds = new Set();
    const creatorsWithContent = new Set();
    const platformStats = {};
    const avgCcvList = [];
    const peakCcvList = [];
    let totalViews = 0, youtubeViews = 0, tiktokViews = 0, instagramViews = 0, twitchViews = 0;
    let totalLikes = 0, totalComments = 0, totalShares = 0, totalWatchHours = 0, totalVodViews = 0;
    let totalImpressions = 0;

    for (const c of contentToDisplay) {
      const streamC = isStreamContent(c);
      const views = c.views || 0;
      const vodViews = c.vodViews || 0;
      // Reach: a stream counts by its VOD views, everything else by views — matches
      // the campaign dashboard so "Total Combined Views" reconciles with its pills.
      const reach = reachOf(c);
      totalViews += reach;
      // Impressions = reach × (5x/1x/10x per platform+date). Uses the same
      // reach-based helper as the Impressions sparkline below, so the card value
      // and its trend agree. (This total was previously never computed, so the
      // Impressions card always fell back to 0.)
      totalImpressions += impressionsFor(c, reach);
      totalLikes += c.likes || 0;
      totalComments += c.comments || 0;
      totalShares += c.shares || 0;
      totalWatchHours += c.watchHours || 0; // game-segment (backend sends Content.watchHours)
      const platform = (c.platform || 'other').toLowerCase();
      if (streamC) totalVodViews += vodViews;
      // Per-platform view pills = non-stream views (stream reach is under VOD Views).
      if (!streamC) {
        if (platform === 'youtube') youtubeViews += views;
        else if (platform === 'tiktok') tiktokViews += views;
        else if (platform === 'instagram') instagramViews += views;
      }

      if (c.creator?.id) { creatorIds.add(c.creator.id); creatorsWithContent.add(c.creator.id); }
      if (c.avgCcv) avgCcvList.push(c.avgCcv);
      if (c.peakCcv) peakCcvList.push(c.peakCcv);

      // Platform breakdown by per-piece reach so the bars sum to Total Combined Views.
      if (!platformStats[platform]) platformStats[platform] = { views: 0, content: 0 };
      platformStats[platform].views += reach;
      platformStats[platform].content++;
    }

    const totalCreatorsInCampaign = keyTracking?.summary?.totalCreators || creatorIds.size;
    const creatorsWithCoverage = creatorsWithContent.size;
    const creatorsWithoutCoverage = Math.max(0, totalCreatorsInCampaign - creatorsWithCoverage);
    const coverageRate = totalCreatorsInCampaign > 0 ? ((creatorsWithCoverage / totalCreatorsInCampaign) * 100).toFixed(1) : 0;

    return {
      totalViews, youtubeViews, tiktokViews, instagramViews, twitchViews,
      totalEngagement: totalLikes + totalComments + totalShares,
      totalWatchHours, totalContentCount: contentToDisplay.length,
      impressions: totalImpressions,
      totalCreators: creatorIds.size,
      avgCcv: avgCcvList.length > 0 ? Math.round(avgCcvList.reduce((a, b) => a + b, 0)) : 0,
      peakCcv: peakCcvList.length > 0 ? Math.round(peakCcvList.reduce((a, b) => a + b, 0)) : 0,
      totalVodViews,
      platformBreakdown: Object.entries(platformStats).map(([platform, stats]) => ({
        platform, views: stats.views, contentCount: stats.content,
        percentage: totalViews > 0 ? ((stats.views / totalViews) * 100).toFixed(1) : 0
      })),
      creatorsWithCoverage, creatorsWithoutCoverage, coverageRate
    };
  })();

  const displayStats = filteredStats;
  const dateRangeBounds = getDateRangeBounds();

  // ─── Weekly stats ─────────────────────────────────────────────────────

  const calculateWeeklyStats = () => {
    if (!contentToDisplay || contentToDisplay.length === 0) return null;
    const now = new Date();
    const dayOfWeek = now.getDay();
    const currentWeekStart = new Date(now);
    currentWeekStart.setDate(now.getDate() - dayOfWeek);
    currentWeekStart.setHours(0, 0, 0, 0);
    const currentWeekEnd = new Date(currentWeekStart);
    currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
    currentWeekEnd.setHours(23, 59, 59, 999);
    const lastWeekStart = new Date(currentWeekStart);
    lastWeekStart.setDate(currentWeekStart.getDate() - 7);
    const lastWeekEnd = new Date(currentWeekStart);
    lastWeekEnd.setTime(currentWeekStart.getTime() - 1);

    const thisWeek = contentToDisplay.filter(c => { const d = new Date(c.postDate || c.createdAt); return d >= currentWeekStart && d <= currentWeekEnd; });
    const lastWeek = contentToDisplay.filter(c => { const d = new Date(c.postDate || c.createdAt); return d >= lastWeekStart && d <= lastWeekEnd; });

    const calcWeekStats = (weekContent) => {
      const ids = new Set();
      const avgList = [], peakList = [];
      let views = 0, likes = 0, comments = 0, shares = 0, watchHours = 0, vodViews = 0;
      for (const c of weekContent) {
        views += c.views || 0; likes += c.likes || 0; comments += c.comments || 0;
        shares += c.shares || 0; watchHours += c.watchHours || 0; vodViews += c.vodViews || 0;
        if (c.creator?.id) ids.add(c.creator.id);
        if (c.avgCcv) avgList.push(c.avgCcv);
        if (c.peakCcv) peakList.push(c.peakCcv);
      }
      return {
        views, engagement: likes + comments + shares, watchHours,
        content: weekContent.length, creators: ids.size,
        avgCcv: avgList.length > 0 ? Math.round(avgList.reduce((a, b) => a + b, 0) / avgList.length) : 0,
        peakCcv: peakList.length > 0 ? Math.max(...peakList) : 0, vodViews
      };
    };

    const thisWeekStats = calcWeekStats(thisWeek);
    const lastWeekStats = calcWeekStats(lastWeek);
    const calcChange = (cur, prev) => prev === 0 ? (cur > 0 ? 100 : 0) : Number(((cur - prev) / prev * 100).toFixed(1));
    const fmtShort = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

    return {
      thisWeek: { ...thisWeekStats, dateRange: `${fmtShort(currentWeekStart)} - ${fmtShort(currentWeekEnd)}` },
      lastWeek: { ...lastWeekStats, dateRange: `${fmtShort(lastWeekStart)} - ${fmtShort(lastWeekEnd)}` },
      changes: {
        views: calcChange(thisWeekStats.views, lastWeekStats.views),
        engagement: calcChange(thisWeekStats.engagement, lastWeekStats.engagement),
        watchHours: calcChange(thisWeekStats.watchHours, lastWeekStats.watchHours),
        content: calcChange(thisWeekStats.content, lastWeekStats.content),
        creators: calcChange(thisWeekStats.creators, lastWeekStats.creators),
        avgCcv: calcChange(thisWeekStats.avgCcv, lastWeekStats.avgCcv),
        peakCcv: calcChange(thisWeekStats.peakCcv, lastWeekStats.peakCcv),
        vodViews: calcChange(thisWeekStats.vodViews, lastWeekStats.vodViews)
      }
    };
  };

  const weeklyStats = calculateWeeklyStats();

  // ─── CSV export ───────────────────────────────────────────────────────

  const exportToCSV = async (type) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${apiUrl}/api/client-portal/${accessToken}/export?type=${type}`, {
        method: 'GET', credentials: 'include',
      });
      if (!response.ok) throw new Error(`Export failed: ${response.status}`);
      const csvText = await response.text();
      const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
      const filename = `${type === 'streams' ? 'live_streams' : 'videos'}_${new Date().toISOString().split('T')[0]}.csv`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export CSV. Please try again.');
    }
  };

  // ─── Background ───────────────────────────────────────────────────────

  const getBackgroundStyle = () => {
    const style = { minHeight: '100vh', position: 'relative', backgroundColor: '#0a0a0a' };
    if (gameCoverUrl) {
      style.backgroundImage = `url(${gameCoverUrl})`;
      style.backgroundSize = 'cover';
      style.backgroundPosition = 'center';
      style.backgroundAttachment = 'fixed';
      style.backgroundRepeat = 'no-repeat';
    } else if (theme.backgroundImageUrl && theme.backgroundImageMode !== 'none') {
      style.backgroundImage = `url(${theme.backgroundImageUrl})`;
      style.backgroundSize = 'cover';
      style.backgroundPosition = 'center';
      style.backgroundAttachment = 'fixed';
    }
    return style;
  };

  // ─── Donut chart data builders ────────────────────────────────────────

  const buildCoverageData = () => {
    const covered = dateRange === 'all'
      ? (keyTracking?.summary?.creatorsWithCoverage || 0)
      : (displayStats?.creatorsWithCoverage || 0);
    const pending = dateRange === 'all'
      ? (keyTracking?.summary?.creatorsWithoutCoverage || 0)
      : (displayStats?.creatorsWithoutCoverage || 0);
    const total = covered + pending;
    return {
      data: [
        { name: 'Covered', value: covered, color: '#22c55e', percentage: total > 0 ? ((covered / total) * 100).toFixed(0) : 0 },
        { name: 'Pending', value: pending, color: '#4b5563', percentage: total > 0 ? ((pending / total) * 100).toFixed(0) : 0 },
      ],
      centerValue: total > 0 ? `${((covered / total) * 100).toFixed(0)}%` : '0%',
      centerLabel: 'Coverage',
    };
  };

  const buildPlatformData = () => {
    const breakdown = displayStats?.platformBreakdown || [];
    if (breakdown.length === 0) return null;
    const total = breakdown.reduce((sum, p) => sum + p.views, 0);
    return {
      data: breakdown.map(p => ({
        name: formatPlatformName(p.platform),
        value: p.views,
        color: getPlatformColor(p.platform),
        percentage: p.percentage,
      })),
      centerValue: formatNumber(total),
      centerLabel: 'Total Views',
    };
  };

  const buildRegionData = (contentList) => {
    // Build from content creators so it respects date filters
    const allCreators = keyTracking?.creators || [];

    // Build a lookup from keyTracking for region info
    const creatorRegionLookup = {};
    for (const c of allCreators) {
      creatorRegionLookup[c.id] = c.country || c.region || 'Unknown';
    }

    // Count unique creators from the content list
    const regionCounts = {};
    const seenCreators = new Set();
    for (const c of (contentList || [])) {
      const creatorId = c.creator?.id;
      if (!creatorId || seenCreators.has(creatorId)) continue;
      seenCreators.add(creatorId);
      const region = creatorRegionLookup[creatorId] || c.creator?.country || c.creator?.region || 'Unknown';
      regionCounts[region] = (regionCounts[region] || 0) + 1;
    }

    if (seenCreators.size === 0) return null;
    const sorted = Object.entries(regionCounts).sort((a, b) => b[1] - a[1]);
    const regionColors = ['#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'];
    const total = seenCreators.size;
    const top = sorted.slice(0, 5);
    const otherCount = sorted.slice(5).reduce((sum, [, count]) => sum + count, 0);
    const data = top.map(([name, value], i) => ({
      name, value, color: regionColors[i % regionColors.length],
      percentage: total > 0 ? ((value / total) * 100).toFixed(0) : 0,
    }));
    if (otherCount > 0) {
      data.push({ name: 'Other', value: otherCount, color: '#6b7280',
        percentage: total > 0 ? ((otherCount / total) * 100).toFixed(0) : 0 });
    }
    return { data, centerValue: total.toString(), centerLabel: 'Creators' };
  };

  // ─── Loading / Error states ───────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950" style={{ fontFamily: theme.fontFamily }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: SC_PINK }} />
          <p className="text-white/60">Loading portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-950" style={{ fontFamily: theme.fontFamily }}>
        <div className="text-center max-w-md px-4">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold mb-2 text-white">Access Error</h1>
          <p className="text-white/60">{error}</p>
        </div>
      </div>
    );
  }

  // ─── Derived data ─────────────────────────────────────────────────────

  const filteredCreatorIds = new Set(filteredContent.map(c => c.creator?.id).filter(Boolean));

  const creatorLatestContent = {};
  contentToDisplay.forEach(item => {
    const creatorId = item.creator?.id;
    if (creatorId && item.postDate) {
      const postDate = new Date(item.postDate);
      if (!creatorLatestContent[creatorId] || postDate > creatorLatestContent[creatorId]) {
        creatorLatestContent[creatorId] = postDate;
      }
    }
  });

  const filteredCreators = (keyTracking?.creators?.filter(creator => {
    if (dateRange === 'all') return true;
    return filteredCreatorIds.has(creator.id);
  }) || []).sort((a, b) => {
    const dateA = creatorLatestContent[a.id] || new Date(0);
    const dateB = creatorLatestContent[b.id] || new Date(0);
    return dateB - dateA;
  });

  const liveStreams = contentToDisplay.filter(item =>
    item.contentType === 'stream' || item.contentType === 'vod' || item.platform?.toLowerCase() === 'twitch' || item.platform?.toLowerCase() === 'kick'
  );
  const videos = contentToDisplay.filter(item =>
    item.contentType !== 'stream' && item.contentType !== 'vod' && item.platform?.toLowerCase() !== 'twitch' && item.platform?.toLowerCase() !== 'kick'
  );

  const coverageData = buildCoverageData();
  const platformData = buildPlatformData();
  const regionData = buildRegionData(contentToDisplay);

  // Table header/cell styles
  const thStyle = { color: 'rgba(255,255,255,0.5)', borderColor: 'rgba(255,255,255,0.08)' };
  const thClass = 'px-3 py-2 text-xs font-medium uppercase';
  const tdStyle = { color: 'rgba(255,255,255,0.85)', borderColor: 'rgba(255,255,255,0.06)' };

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <div style={{ ...getBackgroundStyle(), fontFamily: theme.fontFamily }}>
      {/* Dark overlay */}
      <div className="fixed inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,10,26,0.85) 0%, rgba(10,10,26,0.95) 50%, rgba(10,10,26,1) 100%)', zIndex: 0 }} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6 relative" style={{ zIndex: 1 }}>

        {/* ── Header Bar ── */}
        <div className="flex items-center gap-4 py-3">
          {(theme.logoUrl || campaigns?.[0]?.game?.logoUrl) ? (
            <img src={theme.logoUrl || campaigns?.[0]?.game?.logoUrl} alt={portal?.clientName}
              className="h-12 object-contain flex-shrink-0" />
          ) : (
            <div className="text-xl font-bold flex-shrink-0 text-white">{portal?.clientName}</div>
          )}
          <div className="h-8 w-px flex-shrink-0 bg-white/20" />
          <img src="https://streamersconnected.com/wp-content/uploads/2025/04/sc_header_logo.svg"
            alt="Streamers Connected" className="h-8 object-contain flex-shrink-0" />
          {portal?.reportsFolderUrl && (
            <>
              <div className="h-8 w-px flex-shrink-0 bg-white/20" />
              <a href={portal.reportsFolderUrl} target="_blank" rel="noopener noreferrer"
                className="px-3 py-1.5 rounded-md text-sm font-medium transition-colors hover:bg-white/10 flex items-center gap-2 flex-shrink-0 text-white/80 border border-white/20">
                Reports
              </a>
            </>
          )}
        </div>

        {/* ── Section 1: Campaign Overview header + date filter ── */}
        <section className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-white">Campaign Overview</h2>
              <div className="flex items-center gap-1 bg-white/5 rounded-lg p-0.5">
                {[
                  { value: 'organic', label: 'Organic' },
                  { value: 'sponsored', label: 'Paid' },
                ].map(tab => (
                  <button
                    key={tab.value}
                    onClick={() => setContentTab(tab.value)}
                    className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all ${
                      contentTab === tab.value
                        ? 'bg-[#fe2388] text-white shadow-lg shadow-[#fe2388]/30'
                        : 'text-white/50 hover:text-white/80 hover:bg-white/5'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {[
                { value: 'all', label: 'All Time' },
                { value: 'week', label: '7 Days' },
                { value: 'month', label: '30 Days' },
                { value: 'custom', label: 'Custom' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setDateRange(opt.value)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                    dateRange === opt.value
                      ? 'bg-[#fe2388] text-white shadow-lg shadow-[#fe2388]/25'
                      : 'text-white/50 hover:text-white/80 bg-white/5 hover:bg-white/10'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
              {dateRange === 'custom' && (
                <div className="flex items-center gap-2">
                  <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)}
                    className="px-2 py-1 rounded-full border text-xs bg-white/5 border-white/15 text-white/80 focus:outline-none focus:border-[#fe2388]" />
                  <span className="text-xs text-white/30">to</span>
                  <input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)}
                    className="px-2 py-1 rounded-full border text-xs bg-white/5 border-white/15 text-white/80 focus:outline-none focus:border-[#fe2388]" />
                </div>
              )}
            </div>
          </div>

          {/* ── Hero KPI Cards (top 4 with sparklines) ── */}
          {displayStats && (() => {
            // Build sparkline data from content grouped by day (last 14 days)
            const buildSparkline = (metricFn) => {
              if (!contentToDisplay || contentToDisplay.length === 0) return [];
              const days = {};
              const now = new Date();
              for (let i = 13; i >= 0; i--) {
                const d = new Date(now);
                d.setDate(d.getDate() - i);
                days[d.toISOString().slice(0, 10)] = 0;
              }
              for (const c of contentToDisplay) {
                const key = (c.postDate || c.createdAt || '').slice(0, 10);
                if (key in days) days[key] += metricFn(c);
              }
              return Object.values(days).map((v, i) => ({ v }));
            };

            const viewsSparkline = buildSparkline(c => reachOf(c));
            const engagementSparkline = buildSparkline(c => (c.likes || 0) + (c.comments || 0) + (c.shares || 0));
            const impressionsSparkline = buildSparkline(c => impressionsFor(c, reachOf(c)));
            const watchHoursSparkline = buildSparkline(c => c.watchHours || 0);

            const changes = weeklyStats?.changes || {};

            const heroKpis = [
              { label: 'Total Combined Views', value: displayStats.totalViews, accent: SC_PINK, sparkline: viewsSparkline, change: changes.views },
              { label: 'Impressions', value: displayStats.impressions ?? 0, accent: '#f59e0b', tooltip: 'impressions', sparkline: impressionsSparkline, change: changes.views },
              { label: 'Engagement', value: displayStats.totalEngagement, accent: '#22c55e', tooltip: 'engagement', sparkline: engagementSparkline, change: changes.engagement },
              { label: 'Watch Hours', value: displayStats.totalWatchHours, accent: '#3b82f6', tooltip: 'watchHours', sparkline: watchHoursSparkline, change: changes.watchHours },
            ];

            return (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {heroKpis.map((kpi, i) => (
                  <GlassCard key={i} className="p-5 relative overflow-hidden group hover:scale-[1.02] transition-transform duration-300" glow>
                    {/* Accent glow blob */}
                    <div
                      className="absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-20 blur-2xl group-hover:opacity-30 transition-opacity pointer-events-none"
                      style={{ background: kpi.accent }}
                    />
                    {/* Sparkline background */}
                    {kpi.sparkline.length > 0 && (
                      <div className="absolute bottom-0 left-0 right-0 h-12 opacity-30 pointer-events-none">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={kpi.sparkline} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                            <defs>
                              <linearGradient id={`grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={kpi.accent} stopOpacity={0.6} />
                                <stop offset="100%" stopColor={kpi.accent} stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <Area type="monotone" dataKey="v" stroke={kpi.accent} strokeWidth={1.5} fill={`url(#grad-${i})`} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5">
                          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: kpi.accent }} />
                          <span className="text-xs font-medium text-white/60">{kpi.label}</span>
                          {kpi.tooltip && <YouTubeEstimateTooltip metric={kpi.tooltip} />}
                        </div>
                        {kpi.change !== undefined && kpi.change !== 0 && (
                          <span className={`text-xs font-medium flex items-center gap-0.5 ${kpi.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {kpi.change >= 0 ? '↗' : '↘'}{Math.abs(kpi.change)}%
                          </span>
                        )}
                      </div>
                      <div className="text-3xl font-bold text-white mt-2">
                        <AnimatedNumber value={kpi.value} />
                      </div>
                    </div>
                  </GlassCard>
                ))}
              </div>
            );
          })()}

          {/* ── Secondary stats row ── */}
          {displayStats && (
            <div className="flex flex-wrap gap-3">
              {[
                { label: 'Content', value: displayStats.totalContentCount },
                { label: 'Creators', value: displayStats.totalCreators },
                { label: 'Combined CCV', value: displayStats.avgCcv, tooltip: 'watchHours' },
                { label: 'Combined Peak CCV', value: displayStats.peakCcv },
                displayStats.totalVodViews > 0 && { label: 'VOD Views', value: displayStats.totalVodViews },
                displayStats.youtubeViews > 0 && { label: 'YouTube Views', value: displayStats.youtubeViews },
                displayStats.tiktokViews > 0 && { label: 'TikTok Views', value: displayStats.tiktokViews },
                displayStats.instagramViews > 0 && { label: 'Instagram Views', value: displayStats.instagramViews },
              ].filter(Boolean).map((stat, i) => (
                <GlassCard key={i} className="px-4 py-3 text-center flex-1 min-w-[100px] hover:scale-[1.02] transition-transform">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <span className="text-xs text-white/50">{stat.label}</span>
                    {stat.tooltip && <YouTubeEstimateTooltip metric={stat.tooltip} />}
                  </div>
                  <div className="text-lg font-bold text-white">{formatNumber(stat.value)}</div>
                </GlassCard>
              ))}
            </div>
          )}

          {/* ── Paid Content ROI (from per-content cost) ── */}
          {contentTab === 'sponsored' && sponsoredROI && sponsoredROI.paidContent > 0 && (
            <div>
              <h3 className="text-base font-bold text-white mb-2">Paid Content ROI</h3>
              {(sponsoredROI.byCurrency || []).map((cur) => (
                <div key={cur.currency} className="mb-3">
                  {sponsoredROI.byCurrency.length > 1 && <div className="text-xs text-white/40 mb-1">{cur.currency}</div>}
                  <div className="flex flex-wrap gap-3">
                    {[
                      { label: 'Total Spend', value: `${cur.symbol}${formatNumber(Math.round(cur.spent))}` },
                      { label: 'Paid Content', value: cur.paidContent },
                      { label: 'CPV/Hour', value: cur.cpvHr != null ? `${cur.symbol}${cur.cpvHr.toFixed(2)}` : 'N/A' },
                      { label: 'CPM (per 1k views)', value: cur.cpm != null ? `${cur.symbol}${cur.cpm.toFixed(2)}` : 'N/A' },
                    ].map((stat, i) => (
                      <GlassCard key={i} className="px-4 py-3 text-center flex-1 min-w-[120px]">
                        <span className="text-xs text-white/50">{stat.label}</span>
                        <div className="text-lg font-bold mt-1 text-white">{stat.value}</div>
                      </GlassCard>
                    ))}
                  </div>
                </div>
              ))}
              {sponsoredROI.organicContent > 0 && (
                <p className="text-xs text-white/40 mt-1">+ {sponsoredROI.organicContent} organic piece{sponsoredROI.organicContent > 1 ? 's' : ''} delivering {formatNumber(sponsoredROI.organicViews)} extra views at no cost.</p>
              )}
            </div>
          )}

          {/* ── Platform Breakdown ── */}
          {displayStats?.platformBreakdown && displayStats.platformBreakdown.length > 0 && (
            <>
              <h3 className="text-base font-bold text-white mt-1">Platform Breakdown</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {displayStats.platformBreakdown.map(platform => {
                  const color = getPlatformColor(platform.platform);
                  const totalViews = displayStats.totalViews || 1;
                  const pct = ((platform.views / totalViews) * 100).toFixed(1);
                  return (
                    <GlassCard key={platform.platform} className="p-4" style={{ borderBottom: `3px solid ${color}` }}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <PlatformIcon platform={platform.platform} size="md" />
                          <span className="text-sm text-white/70">{formatPlatformName(platform.platform)}</span>
                        </div>
                        <span className="text-xs text-white/40">{platform.contentCount} pieces</span>
                      </div>
                      <div className="text-xl font-bold text-white mb-2">{formatNumber(platform.views)}</div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                        </div>
                        <span className="text-xs font-medium" style={{ color }}>{pct}%</span>
                      </div>
                    </GlassCard>
                  );
                })}
              </div>
            </>
          )}

          {/* Data freshness */}
          <p className="text-xs text-white/25">
            YouTube &amp; Instagram data last refreshed: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
          </p>
        </section>

        {/* ── Section 2: Insights (Donut Charts) ── */}
        {displayStats && (
          <section>
            <h2 className="text-lg font-bold text-white mb-3">Insights</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <DonutChart {...coverageData} title="Creator Coverage" />
              {platformData && <DonutChart {...platformData} title="Platform Split" />}
              {regionData && <DonutChart {...regionData} title="Region Breakdown" />}
            </div>
          </section>
        )}

        {/* ── Section 3: Weekly Comparison ── */}
        {weeklyStats && (
          <section>
            <GlassCard className="overflow-hidden">
              <div className="px-4 py-3 border-b border-white/10">
                <h3 className="text-sm font-semibold text-white">Weekly Comparison</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-2 px-4 text-white/40"></th>
                      {['Views', 'Content', 'Watch Hrs', 'Engagement', 'Creators', 'Avg CCV', 'Peak CCV', 'VOD Views'].map(label => (
                        <th key={label} className="text-right py-2 px-2 text-white/40">
                          {(label === 'Watch Hrs' || label === 'Engagement') ? (
                            <div className="flex items-center justify-end gap-1">
                              <span>{label}</span>
                              <YouTubeEstimateTooltip metric={label === 'Watch Hrs' ? 'watchHours' : 'engagement'} />
                            </div>
                          ) : label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-white/5">
                      <td className="py-2 px-4 font-medium text-white">
                        This Week <span className="text-white/40 font-normal">({weeklyStats.thisWeek.dateRange})</span>
                      </td>
                      {['views', 'content', 'watchHours', 'engagement', 'creators', 'avgCcv', 'peakCcv', 'vodViews'].map(key => (
                        <td key={key} className="text-right py-2 px-2 font-bold text-white">{formatNumber(weeklyStats.thisWeek[key])}</td>
                      ))}
                    </tr>
                    <tr>
                      <td className="py-2 px-4 font-medium text-white/60">
                        Last Week <span className="text-white/30 font-normal">({weeklyStats.lastWeek.dateRange})</span>
                      </td>
                      {['views', 'content', 'watchHours', 'engagement', 'creators', 'avgCcv', 'peakCcv', 'vodViews'].map(key => (
                        <td key={key} className="text-right py-2 px-2 text-white/60">{formatNumber(weeklyStats.lastWeek[key])}</td>
                      ))}
                    </tr>
                    <tr className="border-t border-white/10">
                      <td className="py-2 px-4 font-medium text-white/40">Change</td>
                      {['views', 'content', 'watchHours', 'engagement', 'creators', 'avgCcv', 'peakCcv', 'vodViews'].map(key => (
                        <td key={key} className={`text-right py-2 px-2 font-medium ${weeklyStats.changes[key] >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {weeklyStats.changes[key] >= 0 ? '↑' : '↓'}{Math.abs(weeklyStats.changes[key])}%
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </GlassCard>
          </section>
        )}

        {/* ── Section 3.5: Live Now (per campaign) ── */}
        {campaigns.map(campaign => {
          const liveStreamsForCampaign = liveByCampaign[campaign.id];
          if (!liveStreamsForCampaign || liveStreamsForCampaign.length === 0) return null;

          const totalCcv = liveStreamsForCampaign.reduce((sum, s) => sum + (s.viewerCount || 0), 0);
          const totalPeakCcv = liveStreamsForCampaign.reduce((sum, s) => sum + (s.peakViewerCount || 0), 0);

          const platformIcon = (p) => p === 'kick'
            ? <span className="inline-flex items-center gap-1 text-green-400"><svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.5 8.5L13 14l5.5 5.5L16 22l-8-8 8-8z"/><path d="M2 2h4v20H2z"/></svg><span className="text-xs">Kick</span></span>
            : p === 'twitch'
            ? <span className="inline-flex items-center gap-1 text-purple-400"><svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M2.149 0l-1.612 4.119v16.836h5.731v3.045h3.224l3.045-3.045h4.657l6.269-6.269V0H2.149zm19.164 13.612l-3.582 3.582h-5.731l-3.045 3.045v-3.045H4.119V1.792h17.194v11.82zM14.388 5.731h1.792v5.373h-1.792V5.731zm-4.836 0h1.792v5.373H9.552V5.731z"/></svg><span className="text-xs">Twitch</span></span>
            : <span className="inline-flex items-center gap-1 text-red-500"><svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg><span className="text-xs">YouTube</span></span>;

          const liveSince = (started) => {
            if (!started) return '-';
            const ms = Date.now() - new Date(started).getTime();
            const h = Math.floor(ms / 3600000);
            const m = Math.floor((ms % 3600000) / 60000);
            return h > 0 ? `${h}h ${m}m` : `${m}m`;
          };

          return (
            <section key={`live-${campaign.id}`}>
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                  </span>
                  Live Now — {campaign.name} ({liveStreamsForCampaign.length})
                </h2>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-baseline gap-1.5 px-2.5 py-1 rounded-md bg-white/5 border border-white/10">
                    <span className="text-xs uppercase tracking-wide text-white/50">Total Combined CCV</span>
                    <span className="text-sm font-semibold text-white">{totalCcv.toLocaleString()}</span>
                  </span>
                  <span className="inline-flex items-baseline gap-1.5 px-2.5 py-1 rounded-md bg-white/5 border border-white/10">
                    <span className="text-xs uppercase tracking-wide text-white/50">Total Combined Peak CCV</span>
                    <span className="text-sm font-semibold text-white">{totalPeakCcv.toLocaleString()}</span>
                  </span>
                </div>
              </div>
              <GlassCard className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-white/5">
                      <tr>
                        <th className={`${thClass} text-left`} style={thStyle}>Creator</th>
                        <th className={`${thClass} text-left`} style={thStyle}>Region</th>
                        <th className={`${thClass} text-left`} style={thStyle}>Platform</th>
                        <th className={`${thClass} text-left`} style={thStyle}>Live Since</th>
                        <th className={`${thClass} text-left`} style={thStyle}>Stream</th>
                        <th className={`${thClass} text-right`} style={thStyle}>CCV</th>
                        <th className={`${thClass} text-right`} style={thStyle}>Peak CCV</th>
                      </tr>
                    </thead>
                    <tbody>
                      {liveStreamsForCampaign.map(s => (
                        <tr key={s.id} className="hover:bg-white/5 transition-colors border-b border-white/5">
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {s.creatorImage ? (
                                <img src={s.creatorImage} alt="" className="w-6 h-6 rounded-full" />
                              ) : (
                                <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs bg-white/10 text-white/60">
                                  {s.creatorName?.charAt(0) || '?'}
                                </div>
                              )}
                              <div className="min-w-0">
                                <span className="text-sm font-medium text-white">{s.creatorName}</span>
                                {s.creatorHandle && <div className="text-xs text-white/40">{s.creatorHandle}</div>}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap text-sm text-white/70">{s.region || '-'}</td>
                          <td className="px-3 py-1.5 whitespace-nowrap">{platformIcon(s.platform)}</td>
                          <td className="px-3 py-1.5 whitespace-nowrap text-sm text-white/70">{liveSince(s.startedAt)}</td>
                          <td className="px-3 py-1.5 max-w-xs">
                            <a href={s.streamUrl} target="_blank" rel="noopener noreferrer"
                              className="text-sm hover:underline truncate block"
                              style={{ color: SC_PINK }}>
                              {s.title || '(untitled)'}
                            </a>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap text-right text-sm font-medium text-white">
                            {(s.viewerCount || 0).toLocaleString()}
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap text-right text-sm text-white/70">
                            {(s.peakViewerCount || 0).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </GlassCard>
            </section>
          );
        })}

        {/* ── Section 4: Creators Table ── */}
        {keyTracking && filteredCreators.length > 0 && (
          <section>
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-bold text-white">
                Creators ({filteredCreators.length}{dateRange !== 'all' && keyTracking.creators.length !== filteredCreators.length ? ` of ${keyTracking.creators.length}` : ''})
              </h2>
              {dateRange !== 'all' && filteredCreators.length !== keyTracking.creators.length && (
                <span className="text-xs px-2 py-1 rounded bg-white/10 text-white/50">
                  Showing creators with content in {dateRangeBounds?.label}
                </span>
              )}
            </div>
            <GlassCard className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className={`${thClass} text-left`} style={thStyle}>Creator</th>
                      <th className={`${thClass} text-center`} style={thStyle}>Key</th>
                      <th className={`${thClass} text-center`} style={thStyle}>Status</th>
                      <th className={`${thClass} text-right`} style={thStyle}>Content</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCreators.slice(0, showAllCreators ? filteredCreators.length : 3).map(creator => (
                      <tr key={creator.id} className="hover:bg-white/5 transition-colors border-b border-white/5">
                        <td className="px-3 py-1.5 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {creator.profileImageUrl ? (
                              <img src={creator.profileImageUrl} alt="" className="w-6 h-6 rounded-full" />
                            ) : (
                              <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs bg-white/10 text-white/60">
                                {creator.name?.charAt(0) || '?'}
                              </div>
                            )}
                            <div className="min-w-0">
                              <span className="text-sm font-medium text-white">{creator.name}</span>
                              <div className="text-xs text-white/40">{creator.handle}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-1.5 whitespace-nowrap text-center">
                          {creator.keyStatus === 'sent' ? (
                            <span className="text-green-400 text-xs font-mono">{creator.keyCode || '✓'}</span>
                          ) : (
                            <span className="text-white/30">-</span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 whitespace-nowrap text-center">
                          {creator.hasCovered ? (
                            <span className="text-green-400">✓</span>
                          ) : (
                            <span className="text-yellow-400">-</span>
                          )}
                        </td>
                        <td className="px-3 py-1.5 whitespace-nowrap text-right text-sm text-white">
                          {creator.contentCount || 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredCreators.length > 3 && (
                <button onClick={() => setShowAllCreators(!showAllCreators)}
                  className="w-full px-3 py-2 text-center text-xs font-medium hover:bg-white/5 transition-colors border-t border-white/10"
                  style={{ color: SC_PINK }}>
                  {showAllCreators ? 'Show less' : `Show ${filteredCreators.length - 3} more`}
                </button>
              )}
            </GlassCard>
          </section>
        )}

        {/* ── Section 5: Live Streams ── */}
        {liveStreams.length > 0 && (
          <section>
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-bold text-white">Live Streams ({liveStreams.length})</h2>
                {dateRange !== 'all' && (
                  <span className="text-xs px-2 py-1 rounded bg-white/10 text-white/50">
                    Filtered by {dateRangeBounds?.label}
                  </span>
                )}
              </div>
              <button type="button" onClick={() => exportToCSV('streams')}
                className="px-3 py-1.5 text-xs rounded transition-colors hover:bg-white/10 flex items-center gap-1.5 text-white/70 border border-white/20">
                Export CSV
              </button>
            </div>

            <GlassCard className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border-collapse">
                  <thead className="bg-white/5">
                    <tr>
                      <th className={`${thClass} text-left`} style={thStyle}>Creator</th>
                      <th className={`${thClass} text-left`} style={thStyle}>Region</th>
                      <th className={`${thClass} text-left`} style={thStyle}>Platform</th>
                      <SortableHeader label="Date" sortKey="date" sortState={streamSort} setSortState={setStreamSort} align="left"
                        thClass={`${thClass} text-left`} thStyle={thStyle} />
                      <th className={`${thClass} text-left`} style={thStyle}>Stream</th>
                      <SortableHeader label="Stream Duration" sortKey="streamDuration" sortState={streamSort} setSortState={setStreamSort}
                        thClass={`${thClass} text-right`} thStyle={thStyle} />
                      <SortableHeader label="Avg CCV" sortKey="avgCcv" sortState={streamSort} setSortState={setStreamSort}
                        tooltip={<YouTubeEstimateTooltip metric="watchHours" />}
                        thClass={`${thClass} text-right`} thStyle={thStyle} />
                      <SortableHeader label="Peak CCV" sortKey="peakCcv" sortState={streamSort} setSortState={setStreamSort}
                        thClass={`${thClass} text-right`} thStyle={thStyle} />
                      <SortableHeader label="Watch Hours" sortKey="watchHours" sortState={streamSort} setSortState={setStreamSort}
                        tooltip={<YouTubeEstimateTooltip metric="watchHours" />}
                        thClass={`${thClass} text-right`} thStyle={thStyle} />
                      <SortableHeader label="VOD Views" sortKey="vodViews" sortState={streamSort} setSortState={setStreamSort}
                        thClass={`${thClass} text-right`} thStyle={thStyle} />
                      <SortableHeader label="Impressions" sortKey="impressions" sortState={streamSort} setSortState={setStreamSort}
                        tooltip={<YouTubeEstimateTooltip metric="impressions" />}
                        thClass={`${thClass} text-right`} thStyle={thStyle} />
                    </tr>
                  </thead>
                  <tbody>
                    {(showAllStreams ? sortItems(liveStreams, streamSort) : sortItems(liveStreams, streamSort).slice(0, 10)).map((item, idx) => (
                      <tr key={item.id} className={idx % 2 === 0 ? '' : 'bg-white/[0.02]'} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <td className="px-3 py-2 whitespace-nowrap" style={tdStyle}>
                          <div className="flex items-center gap-2">
                            {item.creator?.profileImageUrl ? (
                              <img src={item.creator.profileImageUrl} alt="" className="w-6 h-6 rounded-full" />
                            ) : (
                              <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs bg-white/10 text-white/60">
                                {item.creator?.name?.charAt(0) || '?'}
                              </div>
                            )}
                            <span className="font-medium">{item.creator?.name || 'Unknown'}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-white/50" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                          {item.creator?.country || item.creator?.region || '-'}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                          <div className="flex items-center gap-1.5">
                            <PlatformIcon platform={item.platform} size="xs" />
                            <span className="text-white/70">{formatPlatformName(item.platform)}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-white/50" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>{formatDate(item.postDate)}</td>
                        <td className="px-3 py-2" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                          <div className="flex items-center gap-2">
                            {item.thumbnailUrl && (
                              <img src={item.thumbnailUrl} alt="" className="w-16 h-9 rounded object-cover flex-shrink-0" />
                            )}
                            <div className="min-w-0">
                              <a href={item.contentUrl} target="_blank" rel="noopener noreferrer"
                                className="hover:underline block max-w-[200px] truncate" style={{ color: SC_PINK }} title={item.title}>
                                {item.title || 'View Stream'}
                              </a>
                            </div>
                            {item.isPaid && (
                              <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded bg-green-500/20 text-green-400 border border-green-500/30 flex-shrink-0">{item.cost != null ? (({ GBP: String.fromCharCode(163), USD: String.fromCharCode(36), EUR: String.fromCharCode(8364) })[item.costCurrency] || String.fromCharCode(163)) + Number(item.cost).toLocaleString() : "Paid"}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap text-white/85" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>{(() => {
                          const segs = item.segments?.filter(s => s.gameId === item.campaignGameId || s.game?.id === item.campaignGameId);
                          const segDur = (segs && segs.length > 0) ? segs.reduce((sum, s) => sum + (s.durationMinutes || 0), 0) : 0;
                          const dur = segDur || item.streamDuration;
                          return dur ? `${Math.floor(dur / 60)}h ${dur % 60}m` : '-';
                        })()}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap text-white/85" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>{item.avgCcv ? formatNumber(item.avgCcv) : '-'}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap text-white/85" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>{item.peakCcv ? formatNumber(item.peakCcv) : '-'}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap text-white/85" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>{item.watchHours ? formatNumber(item.watchHours) : '-'}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap text-white/85" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>{(item.vodViews || item.views) ? formatNumber(item.vodViews || item.views) : '-'}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap text-white/85" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>{(() => { const imp = impressionsFor(item, reachOf(item)); return imp ? formatNumber(imp) : '-'; })()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {liveStreams.length > 10 && (
                <div className="px-4 py-3 border-t border-white/10 flex justify-between items-center">
                  <span className="text-xs text-white/40">
                    {showAllStreams ? `Showing all ${liveStreams.length} streams` : `Showing 10 of ${liveStreams.length} streams`}
                  </span>
                  <button onClick={() => setShowAllStreams(!showAllStreams)}
                    className="px-4 py-1 text-xs rounded transition-colors hover:opacity-90"
                    style={{ backgroundColor: SC_PINK, color: '#ffffff' }}>
                    {showAllStreams ? 'Show Less' : `Show All ${liveStreams.length}`}
                  </button>
                </div>
              )}
            </GlassCard>
          </section>
        )}

        {/* ── Section 6: Videos ── */}
        {videos.length > 0 && (
          <section>
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-bold text-white">Videos ({videos.length})</h2>
                {dateRange !== 'all' && (
                  <span className="text-xs px-2 py-1 rounded bg-white/10 text-white/50">
                    Filtered by {dateRangeBounds?.label}
                  </span>
                )}
              </div>
              <button type="button" onClick={() => exportToCSV('videos')}
                className="px-3 py-1.5 text-xs rounded transition-colors hover:bg-white/10 flex items-center gap-1.5 text-white/70 border border-white/20">
                Export CSV
              </button>
            </div>

            <GlassCard className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border-collapse">
                  <thead className="bg-white/5">
                    <tr>
                      <th className={`${thClass} text-left`} style={thStyle}>Creator</th>
                      <th className={`${thClass} text-left`} style={thStyle}>Region</th>
                      <th className={`${thClass} text-left`} style={thStyle}>Platform</th>
                      <SortableHeader label="Date" sortKey="date" sortState={videoSort} setSortState={setVideoSort} align="left"
                        thClass={`${thClass} text-left`} thStyle={thStyle} />
                      <th className={`${thClass} text-left`} style={thStyle}>Video</th>
                      <SortableHeader label="Views" sortKey="views" sortState={videoSort} setSortState={setVideoSort}
                        thClass={`${thClass} text-right`} thStyle={thStyle} />
                      <SortableHeader label="Likes" sortKey="likes" sortState={videoSort} setSortState={setVideoSort}
                        thClass={`${thClass} text-right`} thStyle={thStyle} />
                      <SortableHeader label="Comments" sortKey="comments" sortState={videoSort} setSortState={setVideoSort}
                        thClass={`${thClass} text-right`} thStyle={thStyle} />
                      <SortableHeader label="Engagement" sortKey="engagementRate" sortState={videoSort} setSortState={setVideoSort}
                        tooltip={<YouTubeEstimateTooltip metric="engagement" />}
                        thClass={`${thClass} text-right`} thStyle={thStyle} />
                      <SortableHeader label="Impressions" sortKey="impressions" sortState={videoSort} setSortState={setVideoSort}
                        tooltip={<YouTubeEstimateTooltip metric="impressions" />}
                        thClass={`${thClass} text-right`} thStyle={thStyle} />
                    </tr>
                  </thead>
                  <tbody>
                    {(showAllVideos ? sortItems(videos, videoSort) : sortItems(videos, videoSort).slice(0, 10)).map((item, idx) => (
                      <tr key={item.id} className={idx % 2 === 0 ? '' : 'bg-white/[0.02]'} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <td className="px-3 py-2 whitespace-nowrap" style={tdStyle}>
                          <div className="flex items-center gap-2">
                            {item.creator?.profileImageUrl ? (
                              <img src={item.creator.profileImageUrl} alt="" className="w-6 h-6 rounded-full" />
                            ) : (
                              <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs bg-white/10 text-white/60">
                                {item.creator?.name?.charAt(0) || '?'}
                              </div>
                            )}
                            <span className="font-medium">{item.creator?.name || 'Unknown'}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-white/50" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                          {item.creator?.country || item.creator?.region || '-'}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                          <div className="flex items-center gap-1.5">
                            <PlatformIcon platform={item.platform} size="xs" />
                            <span className="text-white/70">{formatPlatformName(item.platform)}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-white/50" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>{formatDate(item.postDate)}</td>
                        <td className="px-3 py-2" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                          <div className="flex items-center gap-2">
                            {item.thumbnailUrl && (
                              <img src={item.thumbnailUrl} alt="" className="w-16 h-9 rounded object-cover flex-shrink-0" />
                            )}
                            <div className="min-w-0">
                              <a href={item.contentUrl} target="_blank" rel="noopener noreferrer"
                                className="hover:underline block max-w-[200px] truncate" style={{ color: SC_PINK }} title={item.title}>
                                {item.title || 'View Video'}
                              </a>
                            </div>
                            {item.isPaid && (
                              <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded bg-green-500/20 text-green-400 border border-green-500/30 flex-shrink-0">{item.cost != null ? (({ GBP: String.fromCharCode(163), USD: String.fromCharCode(36), EUR: String.fromCharCode(8364) })[item.costCurrency] || String.fromCharCode(163)) + Number(item.cost).toLocaleString() : "Paid"}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap text-white/85" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>{item.views ? formatNumber(item.views) : '-'}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap text-white/85" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>{item.likes ? formatNumber(item.likes) : '-'}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap text-white/85" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>{item.comments ? formatNumber(item.comments) : '-'}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap text-white/85" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>{item.engagementRate ? `${item.engagementRate.toFixed(1)}%` : '-'}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap text-white/85" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>{(() => { const imp = impressionsFor(item, reachOf(item)); return imp ? formatNumber(imp) : '-'; })()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {videos.length > 10 && (
                <div className="px-4 py-3 border-t border-white/10 flex justify-between items-center">
                  <span className="text-xs text-white/40">
                    {showAllVideos ? `Showing all ${videos.length} videos` : `Showing 10 of ${videos.length} videos`}
                  </span>
                  <button onClick={() => setShowAllVideos(!showAllVideos)}
                    className="px-4 py-1 text-xs rounded transition-colors hover:opacity-90"
                    style={{ backgroundColor: SC_PINK, color: '#ffffff' }}>
                    {showAllVideos ? 'Show Less' : `Show All ${videos.length}`}
                  </button>
                </div>
              )}
            </GlassCard>
          </section>
        )}

        {/* No content */}
        {contentToDisplay.length === 0 && (
          <section>
            <GlassCard className="p-12 text-center">
              <div className="text-4xl mb-4">🎬</div>
              <p className="text-white/40">{dateRange === 'all' ? 'No content available yet' : 'No content in this date range'}</p>
            </GlassCard>
          </section>
        )}

        {/* ── Section 7: Documents ── */}
        {documents.length > 0 && (
          <section>
            <h2 className="text-lg font-bold text-white mb-3">Documents & Reports</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {documents.map(doc => (
                <GlassCard key={doc.id} className="p-4 hover:bg-white/[0.08] transition-colors cursor-pointer"
                  onClick={() => {
                    if (doc.externalUrl) window.open(doc.externalUrl, '_blank');
                    else if (doc.filename) window.open(clientPortalPublicAPI.getDocumentFileUrl(accessToken, doc.id), '_blank');
                  }}>
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">
                      {doc.documentType === 'key_document' ? '📄' : doc.documentType === 'pdf_report' ? '📊' : doc.documentType === 'google_sheet' ? '📗' : '📁'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-white truncate">{doc.title}</h3>
                      {doc.description && <p className="text-sm mt-1 text-white/50 line-clamp-2">{doc.description}</p>}
                      <div className="text-xs mt-2 text-white/30">
                        {doc.externalUrl ? 'Open in new tab' : `Download ${doc.filename}`}
                      </div>
                    </div>
                  </div>
                </GlassCard>
              ))}
            </div>
          </section>
        )}

      </main>

      {/* ── Compliance Footer ── */}
      <footer className="border-t border-white/5 mt-12 relative" style={{ zIndex: 1, background: 'rgba(10,10,26,0.9)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center space-y-2">
            <p className="text-sm text-white/40">
              Campaign Performance Portal &bull; {portal?.clientName}
            </p>
            <p className="text-xs text-white/25">
              YouTube data provided by YouTube API Services.{' '}
              <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/40">YouTube Terms of Service</a>
              {' '}&bull;{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/40">Google Privacy Policy</a>
            </p>
            <p className="text-xs text-white/25">
              Instagram data provided by Meta&rsquo;s Instagram Graph API.{' '}
              <a href="https://developers.facebook.com/terms/" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/40">Meta Platform Terms</a>
              {' '}&bull;{' '}
              <a href="https://privacycenter.instagram.com/policy" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/40">Instagram Privacy Policy</a>
            </p>
            <p className="text-xs text-white/20">
              Metrics marked with tooltips are calculated by Streamers Connected and may differ from official platform analytics.
            </p>
          </div>
        </div>
      </footer>

      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default ClientPortalPublicV2;
